import { DefaultModal } from 'UI';
import { CourseDto, CourseDtoTypeEnum, PaymentDtoTypeEnum } from 'api/generated';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'store';
import styled, { css, keyframes } from 'styled-components';

import { cdnIconForSlug, certCodeForSlug } from '../BundleBuilderModal/bundleBuilderConfig';
import {
  BundleBuilderContext,
  RelatedBundle,
  ownedCertIdsFrom,
  useBundleBuilderContext,
} from '../BundleBuilderModal/useBundleBuilderContext';
import { useUpsellAccent } from '../BundleBuilderModal/useUpsellAccent';

export const ROOKIE_CERTIFICATIONS = [
  {
    id: '2b91e4ec-aed2-4542-b0ea-40d49d31bed5',
    slug: 'cpt',
    code: 'CPT',
    name: 'IPTA CPT',
    title: 'Certified Personal Trainer',
    accent: '#ED5A26',
  },
  {
    id: '38e739a3-8852-44d8-8da6-423af16d7fb7',
    slug: 'cnc',
    code: 'CNS',
    name: 'IPTA CNS',
    title: 'Certified Nutrition Coach',
    accent: '#22be74',
  },
  {
    id: '717e4783-53fd-4527-8679-94563b1f9535',
    slug: 'cbs',
    code: 'CBS',
    name: 'IPTA CBS',
    title: 'Certified Bodybuilding Specialist',
    accent: '#38B6FF',
  },
  {
    id: 'b0663c77-ab87-4c90-a0e8-cd8538fe2169',
    slug: 'ces',
    code: 'CES',
    name: 'IPTA CES',
    title: 'Corrective Exercise Specialist',
    accent: '#8B5E3C',
  },
  {
    id: '0182eefc-e96c-44f0-8768-80b3efb9fd2d',
    slug: 'csc',
    code: 'SCS',
    name: 'IPTA SCS',
    title: 'Strength & Conditioning Coach',
    accent: '#8B5CF6',
  },
  {
    id: 'affb8b9d-67f0-4460-99e5-c009cb41d830',
    slug: 'gx',
    code: 'GES',
    name: 'IPTA GES',
    title: 'Group Exercise Specialist',
    accent: '#F5B700',
  },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sourceCourseId: string | null;
  /** "Buy the bundle you're trialling" — hands the trialled bundle straight to the payment step. */
  onBuyBundle: (bundleId: string) => void;
};

/**
 * The branch point of both upgrade flows.
 *
 * Every Upgrade CTA lands here first, and the two options it shows depend on the learner's
 * server-resolved bundle context, not on the course:
 *
 *   single-course learner   ->  "Upgrade <course>"            / "Build a bundle"
 *   bundle trialist         ->  "Buy the bundle you're on"    / "Change your bundle"
 *
 * The two cards sit side by side and each one argues its own case — the learner should be able to
 * pick from the benefit lines alone, without reading anything twice. Special states get an honest
 * message instead of a dead end: someone who owns everything is told so, and someone mid-plan is
 * pointed at support rather than being offered a second plan checkout would reject anyway.
 */
const UpgradeChooserModal = ({ isOpen, onClose, sourceCourseId, onBuyBundle }: Props) => {
  const dispatch = useDispatch();
  const courses = useSelector(selectors.courses.selectCourses);
  const { context, isLoading, error } = useBundleBuilderContext(isOpen);
  const { vars: accentVars } = useUpsellAccent();

  const sourceCourse = useMemo(
    () => (courses || []).find((c) => c.id === sourceCourseId) ?? null,
    [courses, sourceCourseId],
  );
  const courseName = sourceCourse?.name ?? 'this course';

  const openPricing = () => {
    // The "upgrade this course" half: the existing pricing table for the course they came from.
    if (sourceCourse) dispatch(actions.courses.selectCourse(sourceCourse as CourseDto));
    dispatch(actions.courses.switchFromChooserToPricing());
  };

  const openBuilder = (seedBundle?: RelatedBundle | null) => {
    if (!sourceCourseId) return;
    dispatch(
      actions.courses.switchFromChooserToBuilder({
        sourceCourseId,
        seed: seedBundle ? { tier: seedBundle.tier, memberSlugs: seedBundle.memberSlugs } : null,
        ownedCertIds: ownedCertIdsFrom(context),
      }),
    );
  };

  // One chip per certification, its icon before its own name — the builder's cert-chip pattern.
  const certChips = (slugs: string[]) =>
    slugs.length > 0 ? (
      <CertChips>
        {slugs.map((slug) => (
          <CertChip key={slug}>
            <CertChipIcon src={cdnIconForSlug(slug)} alt="" loading="lazy" />
            {certCodeForSlug(slug)}
          </CertChip>
        ))}
      </CertChips>
    ) : null;

  const sourceSlug = (sourceCourse as { slug?: string } | null)?.slug;

  const upgradeCourseCard = (primary: boolean) => (
    <OptionCard type="button" $primary={primary} onClick={openPricing}>
      <OptionTitle>
        {sourceSlug && <TitleIcon src={cdnIconForSlug(sourceSlug)} alt="" loading="lazy" />}
        Upgrade {courseName}
      </OptionTitle>
      <OptionTagline>Stick with the course you&apos;re in</OptionTagline>
      <Benefits>
        <li>Unlock every study tool for this course</li>
        <li>Pick the plan level that fits your budget</li>
        <li>Your progress stays exactly where it is</li>
      </Benefits>
      <OptionCta $primary={primary}>See plans &amp; pricing →</OptionCta>
    </OptionCard>
  );

  const renderOptions = (ctx: BundleBuilderContext) => {
    if (ctx.ownsEverything) {
      return (
        <Message>
          <strong>You already own every certification we sell.</strong>
          <br />
          There is nothing left to upgrade — go get certified!
        </Message>
      );
    }

    if (ctx.activeInstallmentBundle) {
      return (
        <>
          <Message>
            You&apos;re currently paying off <strong>{ctx.activeInstallmentBundle.name}</strong>. To change your package
            while a payment plan is running, please contact support so your plan can be adjusted first.
          </Message>
          {sourceCourse && <SingleColumn>{upgradeCourseCard(true)}</SingleColumn>}
        </>
      );
    }

    if (ctx.trialBundle) {
      const trial = ctx.trialBundle;
      const certCount = trial.memberCourseIds.length;
      return (
        <Options>
          <OptionCard type="button" $primary onClick={() => onBuyBundle(trial.bundleId)}>
            <Badge>Fastest</Badge>
            <OptionTitle>Buy the bundle you&apos;re trialling</OptionTitle>
            {/* Chips are the contents; repeating the bundle name above them printed the codes twice. */}
            {trial.memberSlugs.length > 0 ? certChips(trial.memberSlugs) : <OptionTagline>{trial.name}</OptionTagline>}
            <Benefits>
              <li>Everything you&apos;ve been using, unlocked in full</li>
              <li>
                All {certCount} certification{certCount === 1 ? '' : 's'} — progress carries straight over
              </li>
              <li>Done in about a minute</li>
            </Benefits>
            <OptionCta $primary>Choose how to pay →</OptionCta>
          </OptionCard>

          <OptionCard type="button" onClick={() => openBuilder(trial)}>
            <OptionTitle>Change your bundle</OptionTitle>
            <OptionTagline>Not quite the right mix? Swap it first</OptionTagline>
            <Benefits>
              <li>Swap specialties or move up to a bigger package</li>
              <li>Your CPT stays locked in</li>
              <li>See exactly what each package includes and saves</li>
            </Benefits>
            <OptionCta>Open the builder →</OptionCta>
          </OptionCard>
        </Options>
      );
    }

    // A paid bundle owner changing their package starts from what they own.
    const ownedSeed = ctx.paidBundles.find((b) => b.tier != null) ?? ctx.paidBundles[0] ?? null;

    return (
      <Options>
        {sourceCourse && upgradeCourseCard(false)}
        <OptionCard type="button" $primary onClick={() => openBuilder(ownedSeed)}>
          <Badge>Best value</Badge>
          <OptionTitle>{ownedSeed ? 'Upgrade your bundle' : 'Build a bundle'}</OptionTitle>
          <OptionTagline>
            {ownedSeed ? 'Add certifications to what you already own' : 'More certifications, lower price per cert'}
          </OptionTagline>
          <Benefits>
            {ownedSeed ? (
              <>
                <li>Start from the bundle you already have</li>
                <li>Certifications you own are clearly marked</li>
                <li>Go bigger in one step, one payment</li>
              </>
            ) : (
              <>
                <li>Bundle up and save vs buying separately</li>
                <li>CPT plus the specialties clients ask for</li>
                <li>One package, one payment — nothing else to manage</li>
              </>
            )}
          </Benefits>
          <OptionCta $primary>Open the builder →</OptionCta>
        </OptionCard>
      </Options>
    );
  };

  const [isChoosingRookieCert, setIsChoosingRookieCert] = useState(false);
  const [loadingCertId, setLoadingCertId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setIsChoosingRookieCert(false);
      setLoadingCertId(null);
    }
  }, [isOpen]);

  const handleSelectCertification = async (certId: string, certSlug?: string) => {
    setLoadingCertId(certId);
    try {
      // 1. Check if course is already in Redux store
      let courseToSelect = (courses || []).find((c) => c.id === certId || (certSlug && (c as any).slug === certSlug));

      // 2. If not found or missing prices, fetch public course data
      if (!courseToSelect || !courseToSelect.prices || !courseToSelect.prices.installment) {
        try {
          const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';
          const res = await fetch(`${baseUrl}/courses/${certId}/public`);
          if (res.ok) {
            const data = await res.json();
            if (data && data.id === certId) {
              // The public payload has no payment fields; a cert found only there is one the learner
              // does not hold, so describe it as such (what /courses returns for unowned courses).
              courseToSelect = {
                isActive: false,
                currentPayment: { type: PaymentDtoTypeEnum.None, onPause: false },
                ...(courseToSelect || {}),
                ...data,
                type: CourseDtoTypeEnum.Certificate,
              } as CourseDto;
            }
          }
        } catch (fetchErr) {
          console.error('Error fetching course for Rookie selection:', fetchErr);
        }
      }

      if (courseToSelect) {
        setIsChoosingRookieCert(false);
        dispatch(actions.courses.selectCourse(courseToSelect as CourseDto));
        dispatch(actions.courses.switchFromChooserToPricing());
      }
    } finally {
      setLoadingCertId(null);
    }
  };

  const renderRookieCertPicker = () => (
    <RookiePickerView>
      <RookiePickerHeader>
        <BackButton type="button" onClick={() => setIsChoosingRookieCert(false)}>
          ← Back to options
        </BackButton>
        <RookiePickerSubtitle>
          Select one of the 6 certifications below to view Rookie and MVP plans:
        </RookiePickerSubtitle>
      </RookiePickerHeader>

      <RookieCertGrid>
        {ROOKIE_CERTIFICATIONS.map((cert) => {
          const isLoadingThis = loadingCertId === cert.id;
          return (
            <RookieCertCard
              key={cert.id}
              type="button"
              disabled={Boolean(loadingCertId)}
              $accentColor={cert.accent}
              onClick={() => handleSelectCertification(cert.id, cert.slug)}>
              <RookieCertIconWrapper $accentColor={cert.accent}>
                <RookieCertIcon src={cdnIconForSlug(cert.slug)} alt="" loading="lazy" />
              </RookieCertIconWrapper>
              <RookieCertDetails>
                <RookieCertBadge $accentColor={cert.accent}>{cert.code}</RookieCertBadge>
                <RookieCertName>{cert.title}</RookieCertName>
              </RookieCertDetails>
              <RookieCertArrow>{isLoadingThis ? '…' : '→'}</RookieCertArrow>
            </RookieCertCard>
          );
        })}
      </RookieCertGrid>
    </RookiePickerView>
  );

  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onClose}
      title={isChoosingRookieCert ? 'Choose a certification' : 'What would you like to do?'}
      containerCSS={ContainerCSS}
      headerContainerCSS={HeaderCSS}
      titleCSS={TitleCSS}
      closeButtonCSS={CloseCSS}>
      <Body style={accentVars as React.CSSProperties}>
        {isChoosingRookieCert ? (
          renderRookieCertPicker()
        ) : (
          <>
            {/* Skeletons occupy the exact footprint of the real cards, so the modal opens at its
                final size and the context fetch resolving never moves anything. */}
            {isLoading && (
              <Options>
                <SkeletonCard aria-hidden>
                  <SkeletonLine $w="60%" $h={17} />
                  <SkeletonLine $w="45%" $h={13} />
                  <SkeletonGap />
                  <SkeletonLine $w="90%" $h={13} />
                  <SkeletonLine $w="80%" $h={13} />
                  <SkeletonLine $w="85%" $h={13} />
                  <SkeletonFooter />
                </SkeletonCard>
                <SkeletonCard aria-hidden>
                  <SkeletonLine $w="55%" $h={17} />
                  <SkeletonLine $w="50%" $h={13} />
                  <SkeletonGap />
                  <SkeletonLine $w="85%" $h={13} />
                  <SkeletonLine $w="90%" $h={13} />
                  <SkeletonLine $w="75%" $h={13} />
                  <SkeletonFooter />
                </SkeletonCard>
              </Options>
            )}
            {/* On context failure the builder path stays reachable rather than dead-ending the flow. */}
            {!isLoading && error && (
              <Options>
                {sourceCourse && upgradeCourseCard(false)}
                <OptionCard type="button" $primary onClick={() => openBuilder(null)}>
                  <Badge>Best value</Badge>
                  <OptionTitle>Build a bundle</OptionTitle>
                  <OptionTagline>More certifications, lower price per cert</OptionTagline>
                  <Benefits>
                    <li>Bundle up and save vs buying separately</li>
                    <li>CPT plus the specialties clients ask for</li>
                    <li>One package, one payment — nothing else to manage</li>
                  </Benefits>
                  <OptionCta $primary>Open the builder →</OptionCta>
                </OptionCard>
              </Options>
            )}
            {!isLoading && !error && context && renderOptions(context)}
            {!isLoading && (
              <RookiePromptFooter>
                <RookiePromptText>Looking for a single rookie certification?</RookiePromptText>
                <RookieYesButton type="button" onClick={() => setIsChoosingRookieCert(true)}>
                  Yes
                </RookieYesButton>
              </RookiePromptFooter>
            )}
          </>
        )}
      </Body>
    </DefaultModal>
  );
};

export default UpgradeChooserModal;

const ContainerCSS = css`
  max-width: 780px;
  width: 94vw;
  padding: 32px 36px 36px;
`;

/**
 * The close button is absolutely positioned at the modal's top-right; on a narrow modal the
 * centred title would run underneath it. Reserving its footprint on both sides keeps the title
 * centred AND clear of the button.
 */
const HeaderCSS = css`
  padding: 0 48px;
  margin-bottom: 20px;
`;

const CloseCSS = css`
  top: 26px;
  right: 24px;
`;

/** The default modal title is sized for wide modals and clips here — wrap instead of overflowing. */
const TitleCSS = css`
  font-size: 26px;
  line-height: 1.25;
  white-space: normal;
  text-align: center;
`;

/**
 * Matches the tallest real card (title + tagline + three benefits + CTA). Shared by the cards and
 * the skeletons so the modal's height is identical before and after the context resolves.
 */
const CARD_MIN_HEIGHT = 248;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* One card row, whatever state we're in — the modal never opens short and then grows. */
  min-height: ${CARD_MIN_HEIGHT}px;
  justify-content: center;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SingleColumn = styled.div`
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
`;

const OptionCard = styled.button<{ $primary?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  min-height: ${CARD_MIN_HEIGHT}px;
  padding: 22px 20px 18px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ $primary }) => ($primary ? 'var(--accent-soft, rgba(237, 90, 38, 0.05))' : '#fff')};
  border: 2px solid ${({ $primary }) => ($primary ? 'var(--accent, #ED5A26)' : '#e5e7eb')};

  &:hover {
    border-color: var(--accent, #ed5a26);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px var(--accent-glow, rgba(237, 90, 38, 0.16));
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -10px;
  left: 20px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: var(--accent, #ed5a26);
`;

const OptionTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #141826;
`;

const TitleIcon = styled.img`
  height: 13px;
  width: auto;
  margin-right: 6px;
  vertical-align: -1px;
`;

/** The builder's cert-chip pattern: each certification's mark before its own name. */
const CertChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const CertChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px 2px 4px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  color: #4b5563;
  background: #f3f4f6;
`;

const CertChipIcon = styled.img`
  height: 10px;
  width: auto;
`;

const OptionTagline = styled.div`
  margin-top: 3px;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent, #ed5a26);
`;

const Benefits = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;

  li {
    position: relative;
    padding-left: 22px;
    font-size: 13px;
    line-height: 1.4;
    color: #374151;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 800;
      color: #22be74;
    }
  }
`;

const OptionCta = styled.div<{ $primary?: boolean }>`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${({ $primary }) => ($primary ? 'var(--accent-border, rgba(237, 90, 38, 0.25))' : '#f3f4f6')};
  font-size: 14px;
  font-weight: 800;
  color: ${({ $primary }) => ($primary ? 'var(--accent, #ED5A26)' : '#141826')};
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
`;

const SkeletonCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-height: ${CARD_MIN_HEIGHT}px;
  padding: 22px 20px 18px;
  border-radius: 16px;
  border: 2px solid #f0f1f3;
  background: #fff;
`;

const SkeletonLine = styled.div<{ $w: string; $h: number }>`
  width: ${({ $w }) => $w};
  height: ${({ $h }) => $h}px;
  border-radius: 6px;
  background: #eef0f2;
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonGap = styled.div`
  height: 4px;
`;

const SkeletonFooter = styled.div`
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  height: 14px;
  width: 40%;
  border-radius: 6px;
  background: #eef0f2;
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Message = styled.p`
  margin: 0;
  padding: 8px 4px;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  text-align: center;
`;

const RookiePromptFooter = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f0f1f3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const RookiePromptText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
`;

const RookieYesButton = styled.button`
  background: var(--accent-soft, rgba(237, 90, 38, 0.08));
  color: var(--accent, #ed5a26);
  border: 1.5px solid var(--accent, #ed5a26);
  border-radius: 999px;
  padding: 5px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: var(--accent, #ed5a26);
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(237, 90, 38, 0.25);
  }
`;

const RookiePickerView = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RookiePickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--accent, #ed5a26);
  }
`;

const RookiePickerSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const RookieCertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const RookieCertCard = styled.button<{ $accentColor?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: #fff;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $accentColor }) => $accentColor || 'var(--accent, #ed5a26)'};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const RookieCertIconWrapper = styled.div<{ $accentColor?: string }>`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $accentColor }) => ($accentColor ? `${$accentColor}18` : '#f3f4f6')};
`;

const RookieCertIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const RookieCertDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const RookieCertBadge = styled.span<{ $accentColor?: string }>`
  font-size: 11px;
  font-weight: 800;
  color: ${({ $accentColor }) => $accentColor || 'var(--accent, #ed5a26)'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
`;

const RookieCertName = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #141826;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const RookieCertArrow = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #9ca3af;
  margin-left: auto;
  transition: transform 0.15s ease, color 0.15s ease;

  ${RookieCertCard}:hover & {
    color: var(--accent, #ed5a26);
    transform: translateX(3px);
  }
`;
